import { useRef } from 'react';

import { Building03Icon, Edit02Icon, Location01Icon, Mail01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/FormComponents';

import { billingAddressLine, hasCompleteBillingDetails } from '../../billing-details';
import { useHighlightFlash } from '../../use-highlight-flash';

const DetailRow = ({ icon, value, placeholder, strong = false }) => (
  <div className="flex items-start gap-3 py-1">
    <HugeiconsIcon
      icon={icon}
      size={16}
      strokeWidth={1.75}
      className="mt-[3px] shrink-0 text-text-tertiary"
    />
    <span
      className={cn('min-w-0 break-words text-2xl', {
        'font-semibold text-text-primary': value && strong,
        'text-text-primary': value && !strong,
        'text-text-tertiary': !value,
      })}>
      {value || placeholder}
    </span>
  </div>
);

export const CompanyBillingDetails = ({ entity, onEdit, highlightToken }) => {
  const { t } = useTranslation();
  const detailsRef = useRef(null);
  useHighlightFlash(detailsRef, highlightToken);

  return (
    <aside className="w-full shrink-0 rounded-xl border border-border-primary bg-white px-6 pb-6 pt-4 lg:sticky lg:top-0 lg:w-[36rem]">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="m-0 text-3xl font-semibold text-text-primary">
          {t('company.tabs.billing.companyDetails.title')}
        </h3>
        <Button type="link" small onClick={onEdit}>
          <span className="inline-flex items-center gap-2 text-inherit">
            <HugeiconsIcon icon={Edit02Icon} size={14} strokeWidth={2} />
            {t('company.tabs.billing.companyDetails.edit')}
          </span>
        </Button>
      </div>
      <p className="m-0 mb-4 text-xl text-text-secondary">
        {t('company.tabs.billing.companyDetails.description')}
      </p>
      <div ref={detailsRef} className="-mx-3 rounded-lg px-3 py-1">
        <DetailRow
          icon={Building03Icon}
          value={entity?.invoice_entity_name}
          placeholder={t('company.tabs.billing.companyDetails.noName')}
          strong
        />
        <DetailRow
          icon={Mail01Icon}
          value={entity?.email}
          placeholder={t('company.tabs.billing.companyDetails.noEmail')}
        />
        <DetailRow
          icon={Location01Icon}
          value={billingAddressLine(entity)}
          placeholder={t('company.tabs.billing.companyDetails.noAddress')}
        />
      </div>
      {!hasCompleteBillingDetails(entity) && (
        <p className="m-0 mt-3 text-xl text-text-warning">
          {t('company.tabs.billing.companyDetails.missing')}
        </p>
      )}
    </aside>
  );
};
